import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { LedgerEntry } from '@mlm/shared';
import { labelizeLedgerKind } from '@mlm/shared';
import { fmtPeso, wallet } from '../lib/api';
import { qk } from '../lib/queries';
import { instantDay } from '../lib/format';
import { CoachButton, Eyebrow, ErrorNote, Loading, Screen } from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

/**
 * The member's money, read only. Cash-out and top-up are separate flows and are
 * not here yet.
 *
 * Every figure comes from the server. "Total Earned" is `totalEarned`, never
 * `lifetimeEarned`: for a migrated slot the two differ by the imported legacy
 * offset, and showing the smaller one reads to that member as money going
 * missing.
 */
export function WalletPage() {
  const [slotId, setSlotId] = useState<string | null>(null);

  const slots = useQuery({ queryKey: qk.mySlots, queryFn: wallet.slots, staleTime: 5 * 60_000 });
  const totals = useQuery({ queryKey: qk.walletTotals, queryFn: wallet.totals, staleTime: 60_000 });
  const active = slotId ?? slots.data?.[0]?.id ?? null;
  const balance = useQuery({
    queryKey: qk.walletBalance(active),
    queryFn: () => wallet.balance(active),
    enabled: !slots.isPending,
    staleTime: 60_000,
  });
  const ledger = useQuery({
    queryKey: qk.walletLedger(active),
    queryFn: () => wallet.ledger({ slotId: active ?? undefined, pageSize: 50 }),
    enabled: !slots.isPending,
    staleTime: 60_000,
  });

  const noSlot = !slots.isPending && (slots.data?.length ?? 0) === 0;

  return (
    <Screen eyebrow="Wallet" title="Your wallet" wide>
      <ShopNav />

      {slots.isPending || totals.isPending ? (
        <Loading label="Loading your wallet" />
      ) : slots.error ? (
        <ErrorNote error={slots.error} retry={() => void slots.refetch()} />
      ) : noSlot ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>No wallet yet</Eyebrow>
          <p className="mx-auto mt-2 max-w-sm text-[0.93rem] leading-relaxed text-ink/70">
            A wallet belongs to a membership slot. Once you activate one, your earnings and balance
            appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-px border-b border-border bg-border">
            <Figure label="Available" value={fmtPeso(balance.data?.available ?? '0')} strong />
            <Figure label="Total earned" value={fmtPeso(balance.data?.totalEarned ?? '0')} />
            <Figure label="Pending" value={fmtPeso(balance.data?.pending ?? '0')} />
            <Figure
              label="Slots"
              value={String(totals.data?.slotCount ?? slots.data?.length ?? 0)}
            />
          </div>

          {/* Scrip only appears when the tenant actually uses it. */}
          {(balance.data?.availableGc ?? '0') !== '0.0000' && (
            <div className="grid grid-cols-2 gap-px border-b border-border bg-border">
              <Figure label="Gift credits" value={fmtPeso(balance.data!.availableGc)} />
              <Figure label="Coins" value={balance.data!.availableCoin} />
            </div>
          )}

          {balance.data?.debt && balance.data.debt.settledAt === null && (
            <div className="border-b border-border px-5 py-5">
              <Eyebrow>Outstanding</Eyebrow>
              <p className="mt-2 text-[0.93rem] leading-relaxed text-ink/80">
                {fmtPeso(balance.data.debt.debtRemaining)} of your entry cost is still being paid
                from earnings. Commissions settle this first before reaching your wallet.
              </p>
            </div>
          )}

          {(slots.data?.length ?? 0) > 1 && (
            <div className="border-b border-border px-5 py-4">
              <label className="eyebrow" htmlFor="slot-pick">
                Slot
              </label>
              <select
                id="slot-pick"
                value={active ?? ''}
                onChange={(e) => setSlotId(e.target.value)}
                className="mt-2 min-h-11 w-full border border-[rgba(16,32,26,0.22)] bg-background px-3 text-[0.93rem] text-ink"
              >
                {slots.data!.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.slotCode}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="border-b border-border px-5 py-5">
            <Link to="/wallet/cash-out" className="block">
              <CoachButton kind="gold" block>
                Cash out
              </CoachButton>
            </Link>
            <Link to="/wallet/top-up" className="mt-3 block">
              <CoachButton block>Top up</CoachButton>
            </Link>
          </div>

          <p className="border-b border-border px-5 py-3 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-sage-deep">
            Recent activity
          </p>
          {ledger.isPending ? (
            <Loading label="Loading activity" />
          ) : ledger.error ? (
            <ErrorNote error={ledger.error} retry={() => void ledger.refetch()} />
          ) : (ledger.data?.length ?? 0) === 0 ? (
            <p className="px-5 py-10 text-center text-[0.93rem] text-ink/70">Nothing yet.</p>
          ) : (
            <ul>
              {ledger.data!.map((e) => (
                <LedgerRow key={String(e.id)} entry={e} />
              ))}
            </ul>
          )}
        </>
      )}
    </Screen>
  );
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="bg-background px-5 py-6">
      <Eyebrow>{label}</Eyebrow>
      <p
        className={
          strong
            ? 'mt-1 font-display text-[1.6rem] font-semibold text-ink'
            : 'mt-1 font-display text-[1.25rem] font-semibold text-ink'
        }
      >
        {value}
      </p>
    </div>
  );
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
  const credit = entry.direction === 'C';
  const scrip = entry.currencyKind !== 'cash';
  return (
    <li className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
      <div className="min-w-0">
        <p className="text-[0.93rem] leading-snug text-ink">{labelizeLedgerKind(entry.kind)}</p>
        <p className="mt-1 text-[0.85rem] text-sage-deep">
          {instantDay(entry.createdAt)}
          {entry.orderCode ? ` · ${entry.orderCode}` : ''}
        </p>
      </div>
      <p
        className={
          credit
            ? 'shrink-0 font-display text-[1.05rem] font-semibold text-verde'
            : 'shrink-0 font-display text-[1.05rem] font-semibold text-ink/60'
        }
      >
        {credit ? '+' : '-'}
        {scrip ? entry.amount : fmtPeso(entry.amount)}
      </p>
    </li>
  );
}
